import React, { useState, useEffect, useMemo } from "react";
import "./Hero.css";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaGraduationCap, FaChalkboardTeacher, FaRegEdit, FaArrowRight } from "react-icons/fa";
import { BiLibrary } from "react-icons/bi";

const Hero = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  // Memoize the stats array
  const stats = useMemo(
    () => [
      {
        icon: <FaGraduationCap className="stat-icon text-primary mb-3" />,
        value: 200,
        label: t("stats.currentStudents") || "Current Students",
      },
      {
        icon: <FaChalkboardTeacher className="stat-icon text-success mb-3" />,
        value: 30,
        label: t("stats.teachers") || "Certified Teachers",
      },
      {
        icon: <BiLibrary className="stat-icon text-warning mb-3" />,
        value: 20,
        label: t("stats.courses") || "Approved Courses",
      },
      {
        icon: <FaGraduationCap className="stat-icon text-danger mb-3" />,
        value: 750,
        label: t("stats.graduates") || "Graduate Students",
      },
    ],
    [t]
  );

  const [counts, setCounts] = useState(stats.map(() => 0));

  useEffect(() => {
    const intervals = stats.map((stat, i) => {
      let currentCount = 0;
      return setInterval(() => {
        if (currentCount < stat.value) {
          currentCount += Math.ceil(stat.value / 100);
          setCounts((prev) => {
            const newCounts = [...prev];
            newCounts[i] = Math.min(currentCount, stat.value);
            return newCounts;
          });
        } else {
          clearInterval(intervals[i]);
        }
      }, 25);
    });

    return () => intervals.forEach(clearInterval);
  }, [stats]);

  return (
    <div className={`about-page ${isArabic ? "rtl" : "ltr"}`}>
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title animated-text">
            {t("heroTitle") || "Welcome to Our Platform"}
          </h1>
          <p className="hero-subtitle">
            {t("heroSubtitle") || "Discover courses, resources, and opportunities to learn and grow."}
          </p>
          <div className="hero-actions">
            <Link to="/Courseslist" className="hero-btn hero-btn--primary">
              {t("exploreOurPrograms", "Explore Programs")} <FaArrowRight className="hero-btn__icon" />
            </Link>
            <Link to="/Library" className="hero-btn hero-btn--outline">
              {t("navbar.publication", "Publications")}
            </Link>
            <Link to="/contact" className="hero-btn hero-btn--ghost">
              {t("contact.getInTouch", "Contact us")}
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section py-5">
        <div className="container">
          <div className="heading-line mx-auto"></div>
          <h2 className="text-center mb-5">{t("about.ourServices") || "Our Services"}</h2>
          <div className="row">
            {[
              {
                icon: <FaRegEdit size={50} className="service-icon" />,
                title: t("about.courses") || "Courses (Indoor)",
                description: t("about.coursesDesc") || "Join our indoor face-to-face sessions and learn from experts.",
              },
              {
                icon: <BiLibrary size={50} className="service-icon" />,
                title: t("about.library") || "Library & Programs",
                description: t("about.libraryDesc") || "Explore a vast collection of resources and enroll in top programs worldwide.",
              },
              {
                icon: <FaChalkboardTeacher size={50} className="service-icon" />,
                title: t("about.professors") || "Professors & Diploma",
                description: t("about.professorsDesc") || "Learn from exceptional professors and earn a recognized graduate diploma.",
              },
              {
                icon: <FaGraduationCap size={50} className="service-icon" />,
                title: t("about.achievements") || "Achievements & Opportunities",
                description:
                  t("about.achievementsDesc") || "Join a community with worldwide achievements and endless learning opportunities.",
              },
            ].map((service, index) => (
              <div className="col-md-6 text-center mb-4" key={index}>
                <div className="service-card">
                  {service.icon}
                  <h5 className="mt-3">{service.title}</h5>
                  <p>{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section py-5">
        <div className="container text-center">
          <h2 className="stats-title">{t("statsTitle")}</h2>
          <p className="stats-subtitle">{t("statsSubtitle")}</p>
          <div className="row">
            {stats.map((stat, index) => (
              <div className="col-md-3" key={index}>
                {stat.icon}
                <h3 className="stat-value">{counts[index]}</h3>
                <p className="stat-label">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hero;
